import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            try {
              const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))
              // check for previous reload time not to reload infinitely
              if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
                window.sessionStorage.setItem('nuxt-reload', timeNow)
                window.location.reload(true /* skip cache */)
              }
            } catch {
              // don't throw an error if we have issues reading sessionStorage
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"imagesHost":"https://images.acredo.com","appEnv":"prod","apiHost":"https://api.acredo.com","dataHost":"https://data-acredo.acredo.com","tax":{"at":"20","be":"21","ch":"8,1","cz":"21","de":"19","dk":"25","ee":"24","es":"21","fr":"20","hk":"","is":"24","jp":"","kr":"10","lt":"21","nl":"21","no":"","tw":"","us":"0"},"googleAnalyticsIdDev":{"at":"G-EXBFLDYPT5","au":"","beFr":"G-74H4Q8YQLG","beNl":"G-HF0XBRZDYN","ca":"G-XCD0J8ER5N","ch":"G-L2J0GPLWET","cz":"G-GF6NN53BL4","de":"G-JWWFEJT526","dk":"G-9Z0RLL86PK","ee":"G-GLWW44HMBD","es":"G-FF4Q01PHDK","fr":"G-12FX8Y1FMC","hk":"G-CEQ9LJRLWH","is":"G-88SYQVG6DT","jp":"G-1ET8L9K0HE","kr":"G-57CZ474RWD","lt":"","nl":"G-VG6TGQQZHP","no":"G-X0KQVRPFZQ","sg":"G-RZWXFDMJLM","tw":"G-VNMR9R6E2X","us":"G-6F3HSSV40N"},"googleTagManagerId":{"at":"GTM-TTW4Q9C","au":"","beFr":"GTM-K3MDZJMN","beNl":"GTM-NSJTL7PQ","ch":"GTM-WG45HKS3","cz":"GTM-WXXK7MXP","de":"GTM-5NVF67J9","dk":"GTM-WTZX9VS","ee":"GTM-PJVM2MDX","es":"GTM-TZFLV87K","fr":"GTM-NHW2BQ6S","hk":"GTM-5CDHNVZP","is":"GTM-WL4TJDRW","jp":"GTM-TMP6NFV4","kr":"GTM-WQ92Z368","lt":"","nl":"GTM-MHSTJBMG","no":"GTM-W4GBRVP2","sg":"GTM-M5QL75VM","tw":"GTM-P6WQLKDN","us":"GTM-55VMTP4G"},"googleAnalyticsId":{"at":"G-EXBFLDYPT5","au":"","beFr":"G-74H4Q8YQLG","beNl":"G-HF0XBRZDYN","ca":"G-XCD0J8ER5N","ch":"G-L2J0GPLWET","cz":"G-GF6NN53BL4","de":"G-JWWFEJT526","dk":"G-9Z0RLL86PK","ee":"G-GLWW44HMBD","es":"G-FF4Q01PHDK","fr":"G-12FX8Y1FMC","hk":"G-CEQ9LJRLWH","is":"G-88SYQVG6DT","jp":"G-1ET8L9K0HE","kr":"G-57CZ474RWD","lt":"","nl":"G-VG6TGQQZHP","no":"G-X0KQVRPFZQ","sg":"G-RZWXFDMJLM","tw":"G-VNMR9R6E2X","us":"G-6F3HSSV40N"},"googleWebmasterToolsKey":{"at":"2JAsPxsC51qhKzbx_4hgT2oDKkTn0JmbIfIRGWBu6b0","au":"","beFr":"q_09E6QnFVTZFOFiOW1x4O_BXnV59DC3_ZWNdCtZQSA","beNl":"6DRLw4ROTl1hiWVrGJEx69trekcvogpK1_ZpBMm-hyo","ch":"t-7LMkXFrwHELtymIhzsSgpGwt31kjByqDtoib_c-m4","cz":"XCJr131LhJVHToVnT4dUAd6Y38sbTGQ-K4l1vEMrfgY","de":"N2E-l9DW2tuj2l94WKwj2cut3ZB-f4COdalM0OYLydk","dk":"wkvWpKoCtjjYU9WUdkFChlrdZHLF5cHrnU4RP9N4rzk","ee":"pfYfCJDbWcKArAxT6xT1_qw_YID5-lqqzu7G-7o9Oh8","es":"ys0Vyd-NM7dZWy0EVuJTOChDsAlr6Hi3xI3i0ABnPkM","fr":"z_C7FPkbrhvQYuVb15FwRDa49421vZ9e2PFwQIl-enQ","hk":"79xugO5Eeh_kcRGs7JfouTdgdnWVwaQVprdkUZJKYrc","is":"Qu5B-s1EE6AHiNCywhUZwfoQMzV3pJoYM_TsAn-QOkE","jp":"3cz3xEqz32uhKajPOct90Ud_QsY-pFC8t3p25xqXQC4","kr":"b67dgpJdhDhnOUxIPdoy9XHALYt-34uc7377cXczeII","ls":"","nl":"7Z2-HNdtIXO0Hmu35q1dFCDqZfoMpjMCGjyt5FK_3fc","no":"NJugWIX7mW62lvQChxMtPdxLNZWvBaOxAXmjlAoGGe4","sg":"74VmCLuidyblD2GyMXvLmjvXppIUQzDV9A73swngj8s","tw":"2CVyPh31HKMU8XtTFPSFaqHaDcrzUNRDLQdpmSJr0v4","us":"LiMxmATPbA0a7w2dsOCYB-zvNtc4DIt5pWKB8xVzFzs"},"pinterestPage":{"at":"https://www.pinterest.at/acredotrauringe/","be":"https://www.pinterest.de/acredotrauringe/","beFr":"https://www.pinterest.be/loveringsacredo/","beNl":"https://www.pinterest.de/acredotrauringe/","ch":"https://www.pinterest.ch/acredotrauringe/","cz":"https://www.pinterest.de/acredotrauringe/","de":"https://www.pinterest.de/acredotrauringe/","dk":"https://www.pinterest.de/acredotrauringe/","ee":"https://www.pinterest.de/acredotrauringe/","es":"https://www.pinterest.de/acredotrauringe/","fr":"https://www.pinterest.ch/acredotrauringe/","hk":"https://www.pinterest.de/acredotrauringe/","is":"https://www.pinterest.de/acredotrauringe/","jp":"https://www.pinterest.de/acredotrauringe/","kr":"https://www.pinterest.de/acredotrauringe/","nl":"https://www.pinterest.de/acredotrauringe/","no":"https://www.pinterest.de/acredotrauringe/","tw":"https://www.pinterest.de/acredotrauringe/","us":"https://www.pinterest.de/acredotrauringe/","lt":""},"facebookPage":{"at":"https://www.facebook.com/acredo/","be":"https://www.facebook.com/acredo/","beFr":"https://www.facebook.com/acredo.belgique","beNl":"https://www.facebook.com/acredo/","ch":"https://www.facebook.com/acredo/","cz":"https://www.facebook.com/acredo/","de":"https://www.facebook.com/acredo/","dk":"https://www.facebook.com/acredolounge/","ee":"https://www.facebook.com/acredo/","es":"https://www.facebook.com/acredo/","fr":"https://www.facebook.com/acredo/","hk":"https://www.facebook.com/acredo/","is":"https://www.facebook.com/acredo/","jp":"https://www.facebook.com/acredo/","kr":"https://www.facebook.com/acredo/","nl":"https://www.facebook.com/acredo/","no":"https://www.facebook.com/acredo/","tw":"https://www.facebook.com/acredo/","us":"https://www.facebook.com/acredo/","lt":""},"instagram":{"at":"https://www.instagram.com/acredo_official/","be":"https://www.instagram.com/acredo_official/","beFr":"https://www.instagram.com/loverings.acredo","beNl":"https://www.instagram.com/acredo_official/","ch":"https://www.instagram.com/acredo_official/","cz":"https://www.instagram.com/acredo_official/","de":"https://www.instagram.com/acredo_official/","dk":"https://www.instagram.com/acredo_lounge/","ee":"https://www.instagram.com/acredo_official/","es":"https://www.instagram.com/acredo_official/","fr":"https://www.instagram.com/acredo_official/","hk":"https://www.instagram.com/acredo_official/","is":"https://www.instagram.com/acredo_official/","jp":"https://www.instagram.com/acredo_official/","kr":"https://www.instagram.com/acredo_korea/","nl":"https://www.instagram.com/acredo_official/","no":"https://www.instagram.com/acredo_official/","tw":"https://www.instagram.com/acredo_official/","us":"https://www.instagram.com/acredo.usa/","lt":""},"twitter":{"at":"https://twitter.com/Acredo_Ringe","be":"https://twitter.com/Acredo_Ringe","beFr":"https://twitter.com/Acredo_Ringe","beNl":"https://twitter.com/Acredo_Ringe","ch":"https://twitter.com/Acredo_Ringe","cz":"https://twitter.com/Acredo_Ringe","de":"https://twitter.com/Acredo_Ringe","dk":"https://twitter.com/Acredo_Ringe","ee":"https://twitter.com/Acredo_Ringe","es":"https://twitter.com/Acredo_Ringe","fr":"https://twitter.com/Acredo_Ringe","hk":"https://twitter.com/Acredo_Ringe","is":"https://twitter.com/Acredo_Ringe","jp":"https://twitter.com/Acredo_Ringe","kr":"https://twitter.com/Acredo_Ringe","lt":"https://twitter.com/Acredo_Ringe","nl":"https://twitter.com/Acredo_Ringe","no":"https://twitter.com/Acredo_Ringe","tw":"https://twitter.com/Acredo_Ringe","us":"https://twitter.com/Acredo_Ringe"},"NUXT_ENV_DOMAIN":"acredo-trouwringen.be","NUXT_ENV_DEFAULT_CODE":"beNl"}
    }
    // Only set once

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
